import { createMemo, type Accessor } from "solid-js"
import isIndexListSame from "../../../../rx/signal/equals/isIndexListSame"
import alwaysImmutableEmptyArray from "../../../ldsh/alwaysImmutableEmptyArray"
import isNotNil from "../../../ldsh/isNotNil"
import { immutableEmptyArray } from "../../../lib"
import { byHasFloorElevationAccessorDesc } from "../../entity/EntityElevation/HasFloorElevationAccessor"
import type { TileGraphEdge } from "../TileGraphEdge/TileGraphEdge.type"
import type { TileGraphNode } from "../TileGraphNode/TileGraphNode.type"
import type { TileGraphCorner } from "./TileGraphCorner.type"
import createCornerEightDirectionIndexMemo from "./createCornerEightDirectionIndexMemo"
import createCornerEightDirectionRidgesListMemo from "./createCornerEightDirectionRidgesListMemo"

export default function createTileGraphCornerCollection(
  nodeList: Array<TileGraphNode>,
  width: number,
  height: number
): Array<TileGraphCorner> {
  const cornersGridWdith = width + 1
  const cornersGridHeight = height + 1
  const list: Array<TileGraphCorner> = new Array(cornersGridWdith * cornersGridHeight)

  for (let nodeIndex = 0; nodeIndex < nodeList.length; nodeIndex++) {
    const node0: TileGraphNode = nodeList[nodeIndex]

    const {
      x: nodeX,
      y: nodeY,
    } = node0

    const cornerIndexNW = (nodeX * cornersGridHeight) + nodeY
    const cornerIndexNE = (nodeX * cornersGridHeight) + nodeY + 1
    const cornerIndexSW = cornerIndexNW + cornersGridHeight
    const cornerIndexSE = cornerIndexNE + cornersGridHeight

    /* const cornerNW =*/ firstOrCreate(list, cornerIndexNW, cornersGridWdith)
    /* const cornerNE =*/ firstOrCreate(list, cornerIndexNE, cornersGridWdith)
    /* const cornerSW =*/ firstOrCreate(list, cornerIndexSW, cornersGridWdith)
    /* const cornerSE =*/ firstOrCreate(list, cornerIndexSE, cornersGridWdith)

    /*
    // this should be faster,
    // but produced the wrong result
    // set node0's relationship to it's corners
    cornerNW.node.se = node0
    cornerNE.node.sw = node0
    cornerSW.node.ne = node0
    cornerSE.node.nw = node0

    const { n, s, e, w } = node0.edge

    cornerNW.edge.n = n
    cornerNW.edge.w = w

    cornerNE.edge.n = n
    cornerNE.edge.e = e

    cornerSW.edge.s = s
    cornerSW.edge.w = w

    cornerSE.edge.s = s
    cornerSE.edge.e = e
    */
  }

  /*
  if (DEV) {
    for (let cornerIndex = 0; cornerIndex < list.length; cornerIndex++) {
      assertNodesAndEdgesAreCorrect(list[cornerIndex], width)
    }
  }
  */

  for (let cornerIndex = 0; cornerIndex < list.length; cornerIndex++) {
    const corner = list[cornerIndex]!
    assignNodesAndEdges(corner, nodeList, width)
    
    const { edge: { n: edgeN, s: edgeS, e: edgeE, w: edgeW } } = corner

    const edges : Array<TileGraphEdge> = [edgeN, edgeS, edgeE, edgeW].filter(isNotNil)

    const { node: nodeByLcMap } = corner

    const nodes : Array<TileGraphNode> = [nodeByLcMap.ne, nodeByLcMap.nw, nodeByLcMap.se, nodeByLcMap.sw].filter(isNotNil)

    /*
    * return 2-4 existing edges in order,
    * by elevation desc,
    */
    const getEdgesDesc = createMemo(() => {
      return edges.toSorted(byHasElevationMinAccessorDesc)
    }, immutableEmptyArray as unknown as Array<TileGraphEdge>, {
      equals: isIndexListSame
    })
    corner.edge.desc = getEdgesDesc

    // eslint-disable-next-line solid/reactivity
    const getNodesDesc = nodes.length > 1 ? createMemo(() => {
      return nodes.toSorted(byHasFloorElevationAccessorDesc)
    }) : () => nodes
    corner.node.desc = getNodesDesc
    
    const maxFloorElevation = createMemo(() => {
      let value = -Infinity
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i]!
        value = Math.max(value, node.floor_e())
      }
      return value
    })
    const minFloorElevation = createMemo(() => {
      let value = Infinity
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i]!
        value = Math.min(value, node.floor_e())
      }
      return value
    })
    const floorElevationDiff = createMemo(() => {
      return Math.abs(maxFloorElevation() - minFloorElevation())
    })
    corner.elevation = {
      diff: floorElevationDiff,
      max: maxFloorElevation,
      min: minFloorElevation,
    }
    corner.cdi = createCornerEightDirectionIndexMemo(nodeByLcMap, getNodesDesc)
    corner.ridges = createCornerEightDirectionRidgesListMemo(nodeByLcMap, getNodesDesc)
  }

  return list
}

function assignNodesAndEdges(
  corner: TileGraphCorner,
  nodeList: Array<TileGraphNode>,
  gridWidth: number,
) {
  const {
    x: cx,
    y: cy,
    node: nodes,
    // edge: edges,
  } = corner

  nodes.nw = getNodeByXY(nodeList, cx - 0.5, cy - 0.5, gridWidth)
  nodes.ne = getNodeByXY(nodeList, cx + 0.5, cy - 0.5, gridWidth)
  nodes.sw = getNodeByXY(nodeList, cx - 0.5, cy + 0.5, gridWidth)
  nodes.se = getNodeByXY(nodeList, cx + 0.5, cy + 0.5, gridWidth)
}

function getNodeByXY(
  list: Array<TileGraphNode>,
  x: number,
  y: number,
  gridWidth: number,
): TileGraphNode | null {
  if (x >= 0 && y>=0 && x < gridWidth) {
    const index = x + (y * gridWidth)
    const node = list[index]
    if (node && node.x === x && node.y === y) {
      return node
    }
  }
  return null
}

function firstOrCreate(
  list: Array<TileGraphCorner>,
  index: number,
  gridWidth: number,
): TileGraphCorner {
  if (list[index]) {
    const item = list[index]!

    return item
  }

  const x = (index % gridWidth) - 0.5
  const y = (Math.floor(index / gridWidth)) - 0.5

  const item: TileGraphCorner = {
    index,
    x,
    y,
    edge: {
      n: null,
      s: null,
      e: null,
      w: null,
      desc: alwaysImmutableEmptyArray as unknown as TileGraphCorner['edge']['desc'],
    },
    node: {
      ne: null,
      nw: null,
      se: null,
      sw: null,
      desc: alwaysImmutableEmptyArray as unknown as TileGraphCorner['node']['desc'],
    },
    elevation: null as unknown as TileGraphCorner['elevation'],
    cdi: null as unknown as TileGraphCorner['cdi'],
    ridges: null as unknown as TileGraphCorner['ridges'],
  }

  list[index] = item
  return item
}

interface HasElevationMinAccessor {
  elevation: {
    min: Accessor<number>
  }
}

function byHasElevationMinAccessorDesc(
  a: HasElevationMinAccessor | null,
  b: HasElevationMinAccessor | null): number {
  return (b?.elevation.min() ?? -Infinity) - (a?.elevation.min() ?? -Infinity)
}